import React, { useState, useEffect, useRef } from 'react';
import ReferralPrompt from './ReferralPrompt';
import {
  FaPlane,
  FaMapMarkedAlt,
  FaRoute,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
  FaUtensils,
  FaHotel,
  FaCar,
  FaCalendarCheck
} from 'react-icons/fa';
import { ProgressBar } from './SkeletonCard';

const ItineraryProgress = ({
  requestId,
  itinerary,
  onComplete,
  onViewItinerary,
  tripDetails
}) => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const completionHandled = useRef(false);

  const phases = [
    {
      icon: FaMapMarkedAlt,
      label: 'Analyzing Destinations',
      description: 'Finding the best routes and stops...',
      color: 'from-primary-400 to-primary-500'
    },
    {
      icon: FaRoute,
      label: 'Planning Route',
      description: 'Optimizing your travel path...',
      color: 'from-secondary-400 to-secondary-500'
    },
    {
      icon: FaUtensils,
      label: 'Finding Experiences',
      description: 'Discovering restaurants and activities...',
      color: 'from-accent-400 to-accent-500'
    },
    {
      icon: FaHotel,
      label: 'Curating Accommodations',
      description: 'Selecting the best places to stay...',
      color: 'from-primary-500 to-secondary-400'
    },
    {
      icon: FaCalendarCheck,
      label: 'Finalizing Itinerary',
      description: 'Putting everything together...',
      color: 'from-secondary-400 to-primary-400'
    }
  ];

  // Simulate progress based on elapsed time and status
  useEffect(() => {
    if (!itinerary) return;

    const status = itinerary.status;

    // Handle failed status
    if (status === 'failed') {
      return;
    }

    // Handle completion - animate smoothly to 100%
    if (status === 'completed' && !isCompleting && !completionHandled.current) {
      setIsCompleting(true);
      completionHandled.current = true;

      // Animate remaining progress quickly but smoothly
      const completeAnimation = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(completeAnimation);
            if (onComplete) onComplete();
            return 100;
          }
          // Fast but visible increments to reach 100%
          const remaining = 100 - prev;
          const increment = Math.max(remaining * 0.15, 2);
          const newProgress = Math.min(prev + increment, 100);

          // Update phase based on progress
          const phaseIndex = Math.min(
            Math.floor((newProgress / 100) * phases.length),
            phases.length - 1
          );
          setCurrentPhase(phaseIndex);

          return newProgress;
        });
      }, 150); // Fast updates for smooth animation

      return () => clearInterval(completeAnimation);
    }

    // Don't run simulation if already completing
    if (isCompleting) return;

    // Simulate progress while processing - faster initial progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until actually complete

        // Faster progress at the beginning, slower as it goes
        let increment;
        if (prev < 30) {
          increment = Math.random() * 8 + 4; // 4-12% increments initially
        } else if (prev < 60) {
          increment = Math.random() * 5 + 2; // 2-7% increments mid-way
        } else {
          increment = Math.random() * 3 + 1; // 1-4% increments near end
        }

        const newProgress = Math.min(prev + increment, 90);

        // Update current phase based on progress
        const phaseIndex = Math.min(
          Math.floor((newProgress / 100) * phases.length),
          phases.length - 1
        );
        setCurrentPhase(phaseIndex);

        return newProgress;
      });
    }, 800); // Update every 800ms for visible progress

    return () => clearInterval(interval);
  }, [itinerary?.status, onComplete, phases.length, isCompleting]);

  const isComplete = itinerary?.status === 'completed' && progress >= 100;
  const isFailed = itinerary?.status === 'failed';
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-secondary-50 to-primary-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-secondary-400 mb-4">
            <FaPlane className="text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-800 mb-2">
            {isComplete ? 'Your Itinerary is Ready!' : 'Creating Your Adventure'}
          </h1>
          {tripDetails && (
            <p className="text-neutral-600">
              {tripDetails.start_location} → {tripDetails.end_location}
            </p>
          )}
        </div>

        {/* Progress Card */}
        <div className={`bg-white rounded-2xl shadow-elevated p-8 mb-6 ${
          isComplete ? 'ring-2 ring-secondary-400' : ''
        } ${isFailed ? 'ring-2 ring-red-400' : ''}`}>

          {/* Circular Progress */}
          <div className="flex items-center gap-8 mb-6">
            <div className="relative flex-shrink-0">
              <svg className="w-28 h-28 transform -rotate-90">
                <circle
                  cx="56"
                  cy="56"
                  r="45"
                  stroke="#E4E9ED"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="56"
                  cy="56"
                  r="45"
                  stroke={isComplete ? '#4ECDC4' : isFailed ? '#EF4444' : 'url(#progressGradient)'}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-500 ease-out"
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FF6B6B" />
                    <stop offset="100%" stopColor="#4ECDC4" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-neutral-800">
                  {Math.round(progress)}%
                </span>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {isComplete ? (
                  <FaCheckCircle className="text-secondary-500 text-xl" />
                ) : isFailed ? (
                  <FaExclamationCircle className="text-red-500 text-xl" />
                ) : (
                  <FaSpinner className="text-primary-500 text-xl animate-spin" />
                )}
                <span className="text-lg font-semibold text-neutral-800">
                  {isComplete
                    ? 'Itinerary Complete!'
                    : isFailed
                      ? 'Generation Failed'
                      : phases[currentPhase]?.label || 'Preparing...'}
                </span>
              </div>
              <p className="text-neutral-500 text-sm">
                {isComplete
                  ? 'Your personalized travel plan is ready to explore'
                  : isFailed
                    ? itinerary?.error_message || 'Something went wrong. Please try again.'
                    : phases[currentPhase]?.description || 'Initializing your trip...'}
              </p>
            </div>
          </div>

          {/* Linear Progress Bar */}
          <div className="mb-8">
            <ProgressBar
              progress={progress}
              showPercentage={false}
              size="lg"
              variant={isComplete ? 'success' : isFailed ? 'warning' : 'gradient'}
            />
            {/* Step indicators below progress bar */}
            <div className="flex justify-between mt-3 px-1">
              {phases.map((phase, index) => {
                const PhaseIcon = phase.icon;
                const isPhaseComplete = index < currentPhase || isComplete;
                const isPhaseActive = index === currentPhase && !isComplete && !isFailed;
                return (
                  <div
                    key={index}
                    className="flex flex-col items-center"
                    title={phase.label}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isPhaseComplete
                          ? 'bg-secondary-500 text-white shadow-md'
                          : isPhaseActive
                            ? 'bg-primary-500 text-white shadow-md ring-4 ring-primary-100'
                            : 'bg-neutral-200 text-neutral-400'
                      }`}
                    >
                      {isPhaseComplete ? (
                        <FaCheckCircle className="text-xs" />
                      ) : (
                        <PhaseIcon className="text-xs" />
                      )}
                    </div>
                    <span className={`text-xs mt-1 font-medium hidden sm:block ${
                      isPhaseComplete || isPhaseActive ? 'text-neutral-700' : 'text-neutral-400'
                    }`}>
                      {phase.label.split(' ')[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phase Steps */}
          <div className="space-y-3">
            {phases.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = index === currentPhase && !isComplete && !isFailed;
              const isCompleted = index < currentPhase || isComplete;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-50 to-secondary-50 ring-1 ring-primary-200'
                      : isCompleted
                        ? 'bg-secondary-50'
                        : 'bg-neutral-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-r ${phase.color} shadow-travel`
                      : isCompleted
                        ? 'bg-secondary-400'
                        : 'bg-neutral-200'
                  }`}>
                    {isCompleted && !isActive ? (
                      <FaCheckCircle className="text-white text-sm" />
                    ) : (
                      <PhaseIcon className={`text-sm ${isActive || isCompleted ? 'text-white' : 'text-neutral-400'}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${
                      isActive || isCompleted ? 'text-neutral-800' : 'text-neutral-400'
                    }`}>
                      {phase.label}
                    </span>
                  </div>
                  {isActive && (
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Box */}
        {!isComplete && !isFailed && (
          <div className="bg-gradient-to-r from-accent-400/10 to-accent-500/10 rounded-xl p-4 mb-6 border border-accent-200">
            <div className="flex items-start gap-3">
              <FaCar className="text-accent-500 mt-1 flex-shrink-0" />
              <div>
                <p className="text-sm text-neutral-700">
                  <strong>Planning your perfect trip!</strong> Our AI is analyzing thousands of destinations,
                  reviews, and routes to create your personalized itinerary. This typically takes 1-2 minutes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="flex justify-center gap-4">
          {isComplete && (
            <button
              onClick={onViewItinerary}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-400 text-white font-semibold rounded-xl
                       hover:from-primary-600 hover:to-secondary-500 transform hover:scale-105 transition-all duration-200
                       shadow-travel flex items-center gap-2"
            >
              <FaMapMarkedAlt />
              View My Itinerary
            </button>
          )}
          {isFailed && (
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-3 bg-gradient-to-r from-neutral-600 to-neutral-700 text-white font-semibold rounded-xl
                       hover:from-neutral-700 hover:to-neutral-800 transform hover:scale-105 transition-all duration-200
                       shadow-medium flex items-center gap-2"
            >
              Try Again
            </button>
          )}
        </div>

        {/* The referral ask, at the point of maximum enthusiasm: the plan they
            asked for has just appeared. Renders nothing unless the account has
            an active referral link. */}
        {isComplete && (
          <ReferralPrompt destination={tripDetails?.end_location} />
        )}

        {/* Email Notice */}
        {!isComplete && !isFailed && (
          <p className="text-center text-neutral-500 text-sm mt-6">
            We'll send you an email when your itinerary is ready!
          </p>
        )}
      </div>
    </div>
  );
};

export default ItineraryProgress;
